import { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useSubscription } from '../auth/useSubscription';

export interface SearchResult {
  kind: 'peptide' | 'stack' | 'faq';
  id: string;
  name: string;            // for FAQs this carries the question text
  similarity: number;
  // Library only: top-1 KB chunk excerpt + attribution for the matched
  // peptide. Both are null for stack matches and for FAQ matches.
  chunkExcerpt?: string | null;
  chunkSource?: string | null;
}

export type SearchTable = 'library' | 'faqs';

interface Options {
  debounceMs?: number;
  matchCount?: number;
  similarityThreshold?: number;
}

interface State {
  results: SearchResult[];
  isLoading: boolean;
  error: Error | null;
  /** True when this search surface is Plus-gated for the current user
   *  (library search on a free account). Lets callers render an upgrade
   *  nudge instead of an unexplained empty result list. */
  gated: boolean;
}

const DEFAULT_DEBOUNCE = 300;
const DEFAULT_MATCH_COUNT = 8;
// Tuned for gemini-embedding-001 + RETRIEVAL_QUERY task type. 0.65 keeps
// strong matches and trims off the long tail of moderately-related results
// that turn the list into noise for beginners.
const DEFAULT_SIMILARITY_THRESHOLD = 0.65;
const SESSION_CACHE_MAX = 50;

// Per-session cache: query → results, keyed by `${table}::${normalizedQuery}`.
// Evicts oldest entries when over SESSION_CACHE_MAX.
const sessionCache = new Map<string, SearchResult[]>();

function rememberResults(key: string, results: SearchResult[]): void {
  if (sessionCache.size >= SESSION_CACHE_MAX) {
    const firstKey = sessionCache.keys().next().value;
    if (firstKey !== undefined) sessionCache.delete(firstKey);
  }
  sessionCache.set(key, results);
}

// Test-only: clear the module-scoped cache so suites can isolate state
// across tests. Prefixed __ to mark it as not part of the runtime API.
export function __clearSessionCacheForTesting(): void {
  sessionCache.clear();
}

/**
 * Clear the module-scoped result cache. Library results can contain
 * premium KB excerpts (chunk content is Plus-gated server-side), and the
 * module cache outlives auth state in a long-lived WebView - call this
 * on sign-out / user change (AuthProvider does, alongside
 * clearCloudQueryCache) so one user's premium excerpts never render for
 * the next session's user.
 */
export function clearSemanticSearchCache(): void {
  sessionCache.clear();
}

export function useSemanticSearch(table: SearchTable, query: string, options: Options = {}): State {
  const debounceMs = options.debounceMs ?? DEFAULT_DEBOUNCE;
  const matchCount = options.matchCount ?? DEFAULT_MATCH_COUNT;
  const similarityThreshold = options.similarityThreshold ?? DEFAULT_SIMILARITY_THRESHOLD;

  const [state, setState] = useState<State>({ results: [], isLoading: false, error: null, gated: false });
  const reqIdRef = useRef(0);

  // Library search is Plus-only (KB chunks are premium; the RPC returns
  // zero rows for free users). Skip client-side so free-user keystrokes
  // don't burn embed-query invocations for guaranteed-empty results.
  // FAQ search stays free. Flips live when the subscription changes.
  const subscription = useSubscription();
  const libraryGated = table === 'library' && !subscription.isActive;

  useEffect(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized || libraryGated) {
      setState({ results: [], isLoading: false, error: null, gated: libraryGated });
      return;
    }

    const cacheKey = `${table}::${normalized}`;
    const cached = sessionCache.get(cacheKey);
    if (cached) {
      setState({ results: cached, isLoading: false, error: null, gated: false });
      return;
    }

    const myReqId = ++reqIdRef.current;
    setState((s) => ({ ...s, isLoading: true, error: null }));

    const timer = setTimeout(async () => {
      try {
        const { data: embedData, error: embedErr } = await supabase.functions.invoke<{ embedding: number[] }>(
          'embed-query',
          { body: { q: normalized } },
        );
        if (embedErr || !embedData?.embedding) {
          throw embedErr ?? new Error('no embedding returned');
        }
        if (myReqId !== reqIdRef.current) return; // stale

        // Library uses the chunk-aware RPC so each peptide result carries
        // the top KB chunk excerpt for inline rendering. FAQs use match_faqs
        // since FAQ short_answer text is already on the row in allFaqs.
        const rpcName = table === 'library' ? 'match_library_with_chunks' : 'match_faqs';
        const { data: rows, error: rpcErr } = await supabase.rpc(rpcName, {
          query_embedding: embedData.embedding,
          match_count: matchCount,
          similarity_threshold: similarityThreshold,
        });
        if (rpcErr) throw rpcErr;
        if (myReqId !== reqIdRef.current) return; // stale

        const results: SearchResult[] = (rows ?? []).map((r: Record<string, unknown>) => ({
          kind: (r.kind as SearchResult['kind']) ?? 'faq',
          id: r.id as string,
          name: (r.name as string) ?? (r.question as string) ?? '',
          similarity: r.similarity as number,
          chunkExcerpt: (r.chunk_excerpt as string | null | undefined) ?? null,
          chunkSource: (r.chunk_source as string | null | undefined) ?? null,
        }));

        rememberResults(cacheKey, results);
        setState({ results, isLoading: false, error: null, gated: false });
      } catch (err) {
        if (myReqId !== reqIdRef.current) return;
        setState({ results: [], isLoading: false, error: err as Error, gated: false });
      }
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [table, query, debounceMs, matchCount, similarityThreshold, libraryGated]);

  return state;
}
